import React, { useEffect, useState } from "react";

//UI Material
import {
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";

//Local components
import EntryRow from "./EntryRow";

//Helpers
import { fetchWithTokenJSONPost } from "../../Helpers/network";

//Material Icons
import { ArrowForward, Inventory } from "@mui/icons-material";

export default function EntriesTable({ method = "all" }) {
  // ==========>Component States<==========
  //Entries loader
  const [entriesLoader, setEntriesLoader] = useState(true);
  //State for select page
  const [page, setPage] = useState(0);
  //State for manage rows per page
  const [rowsPerPage, setRowsPerPage] = useState(10);
  //State for setting error when could not get products data
  const [dataError, setDataError] = useState(false);
  //State for entries list
  const [entriesList, setEntriesList] = useState([]);

  // ==========>Component UseEffects<==========
  //The useEffect checks for token validation
  useEffect(() => {
    getEntriesList();
  }, []);

  // ==========>Component Functions<==========
  //Function to manage page selection
  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  //Function to control rows per page options
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  //Function to select the method for data query
  const selectPathAccordingToReceivedMethod = () => {
    switch (method) {
      case "all":
        return `/inventory/get-entries`;
      case "item":
        return `/inventory/get-entries-by-item`;
      case "supplier":
        return `/inventory/get-entries-by-supplier`;
      default:
        return `/inventory/get-entries`;
    }
  };

  //Function to get entries list
  const getEntriesList = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        selectPathAccordingToReceivedMethod(),
        {
          howMany: 2500,
          id: new URLSearchParams(window.location.search).get("id"),
        }
      );
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setEntriesList(jsonResponse.data);
      } else {
        if (requestResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      setDataError(false);
    }
    setEntriesLoader(false);
  };

  //Function to get total amount for items entered
  const getStatsFromEntries = () => {
    let totalAmount = 0;
    let totalTaxesAmount = 0;
    let count = 0;

    for (const item of entriesList) {
      count += item.quantity;
      if (item.taxesIncluded) {
        let taxesAmount =
          Number(item.costPerUnit) * (item.taxesPercentage / 100);
        totalTaxesAmount += taxesAmount;
        totalAmount += (Number(item.costPerUnit) - taxesAmount) * item.quantity;
      } else {
        totalAmount += Number(item.costPerUnit) * item.quantity;
      }
    }

    return {
      totalAmount: Number(totalAmount).toFixed(2),
      totalTaxesAmount: Number(totalTaxesAmount).toFixed(2),
      count: Number(count).toFixed(2),
      averagePricePerItem: Number(totalAmount / count).toFixed(2),
    };
  };

  // ==========>Component<==========
  return (
    <>
      <Stack
        justifyContent="flex-start"
        textAlign="left"
        spacing={1}
        sx={{ p: 2 }}
      >
        <Typography variant="h7" fontWeight="bold" color="secondary">
          Resumen de entradas
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              <Typography variant="body2" fontWeight="bold">
                Total entradas registradas
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                textAlign="left"
                spacing={1}
              >
                <ArrowForward fontSize="12px" color="success" />
                <Typography variant="body2">{entriesList.length}</Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              <Typography variant="body2" fontWeight="bold">
                Total ítems ingresados
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
                textAlign="left"
                spacing={1}
              >
                <Inventory fontSize="12px" color="success" />
                <Typography variant="body2">
                  {getStatsFromEntries().count}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              <Typography variant="body2" fontWeight="bold">
                Total pagado por ítems (sin impuestos)
              </Typography>
              <Typography variant="body2">
                {`$ ${getStatsFromEntries().totalAmount}`}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              <Typography variant="body2" fontWeight="bold">
                Total pagado por impuestos
              </Typography>
              <Typography variant="body2">
                {`$ ${getStatsFromEntries().totalTaxesAmount}`}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              <Typography variant="body2" fontWeight="bold">
                Precio promedio por ítem
              </Typography>
              <Typography variant="body2">
                {`$ ${getStatsFromEntries().averagePricePerItem}`}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Stack
              alignItems="flex-start"
              justifyContent="flex-start"
              textAlign="left"
            >
              <Typography variant="body2" fontWeight="bold">
                Precio promedio por impuestos
              </Typography>
              <Typography variant="body2">{0}</Typography>
            </Stack>
          </Grid>
        </Grid>
      </Stack>

      <Divider />
      <Stack
        justifyContent="flex-start"
        textAlign="left"
        spacing={1}
        sx={{ p: 2 }}
      >
        <Typography variant="h7" fontWeight="bold" color="secondary">
          Historial de entradas
        </Typography>
      </Stack>

      <TableContainer>
        <Table aria-label="entries-table">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                  ID
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                  Código
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                  Ítem
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                  Fecha de entrada
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                  Cantidad
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                  Proveedor (ID)
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2" fontWeight="bold">
                  Acciones
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entriesLoader ? (
              <TableCell align="center" colSpan={7}>
                <CircularProgress />
              </TableCell>
            ) : (
              entriesList
                .slice()
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((entry) => <EntryRow key={entry.id} entryData={entry} />)
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={entriesList.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        labelRowsPerPage="Filas"
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
}
